import React, { useEffect, useMemo, useState } from 'react';
import EditOutlined from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import OptionModal from '../../../../../components/Modals/OptionModal.tsx';
import { useFeatureTheme } from '../../../../../theme/hooks/useFeatureTheme';
import { FORM_STUDIO_COPY } from '../../constants/formStudioConfig';
import { mergeOptionColors, normalizeFieldOptions } from '../../utils/fieldOptionsUtils';
import {
  getSubstatusOptionsForParentOption,
  setSubstatusOptionsForParentOption,
} from '../../utils/statusOptionSubstatusUtils';
import FieldOptionsPreview from './FieldOptionsPreview';

export default function StatusOptionSubstatusPanel({
  parentField,
  subStatusField,
  disabled = false,
  onChange,
}) {
  const { semantic, radiusPx, typographyStyles } = useFeatureTheme();
  const copy = FORM_STUDIO_COPY.statusField;

  const statusOptions = useMemo(
    () => normalizeFieldOptions(parentField?.key_options),
    [parentField?.key_options]
  );

  const [expanded, setExpanded] = useState(statusOptions[0] || false);
  const [optionModal, setOptionModal] = useState(null);

  useEffect(() => {
    setExpanded(statusOptions[0] || false);
    setOptionModal(null);
  }, [parentField?.key_name, statusOptions]);

  if (!parentField || !statusOptions.length) {
    return null;
  }

  const handleAccordionChange = (optionName) => (_, isExpanded) => {
    setExpanded(isExpanded ? optionName : false);
  };

  const openOptionEditor = (parentOptionName) => {
    setOptionModal({
      parentOptionName,
      options: getSubstatusOptionsForParentOption(subStatusField, parentOptionName),
    });
  };

  const handleOptionSubmit = (options, info) => {
    if (!optionModal || !onChange) {
      setOptionModal(null);
      return;
    }

    const updated = setSubstatusOptionsForParentOption(
      subStatusField,
      optionModal.parentOptionName,
      options
    );
    onChange({
      ...updated,
      option_colors: mergeOptionColors(
        updated.key_options,
        info,
        updated.option_colors || {}
      ),
    });
    setOptionModal(null);
  };

  return (
    <Box
      sx={{
        p: { xs: 1.25, sm: 1.5 },
        borderRadius: radiusPx.md,
        border: `1px solid ${semantic.borders.border}`,
        bgcolor: semantic.surfaces.surfaceSubtle,
      }}
    >
      <Typography sx={{ ...typographyStyles.bodyText, fontWeight: 600 }}>
        {copy.perOptionTitle}
      </Typography>
      <Typography sx={{ ...typographyStyles.caption, color: semantic.text.secondary, mt: 0.5, mb: 1.5 }}>
        {copy.perOptionCaption}
      </Typography>

      {statusOptions.map((optionName) => {
        const panelId = `status-option-${optionName.replace(/\s+/g, '-')}`;
        const isExpanded = expanded === optionName;

        return (
          <Accordion
            key={optionName}
            expanded={isExpanded}
            disabled={disabled}
            onChange={handleAccordionChange(optionName)}
            disableGutters
            elevation={0}
            sx={{
              mb: 0.75,
              border: `1px solid ${semantic.borders.border}`,
              borderRadius: `${radiusPx.md} !important`,
              '&:before': { display: 'none' },
              bgcolor: semantic.surfaces.surface,
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${panelId}-content`}
              id={`${panelId}-header`}
              sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { my: 0.75 } }}
            >
              <Typography sx={{ ...typographyStyles.bodyText, fontWeight: 600 }}>
                {optionName}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 1.5 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <FieldOptionsPreview
                    keyOptions={getSubstatusOptionsForParentOption(subStatusField, optionName)}
                    optionColors={subStatusField?.option_colors}
                    maxVisible={5}
                    variant="compact"
                    emptyLabel={copy.perOptionEmpty}
                  />
                </Box>
                <Tooltip title={copy.perOptionEditTooltip}>
                  <IconButton
                    size="small"
                    disabled={disabled}
                    onClick={() => openOptionEditor(optionName)}
                    aria-label={`Edit sub-status options for ${optionName}`}
                  >
                    <EditOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}

      <OptionModal
        showOptionModal={Boolean(optionModal)}
        onCancel={() => setOptionModal(null)}
        onSubmit={handleOptionSubmit}
        options={optionModal?.options || []}
        columnInfo={{
          attributeType: 'DROPDOWN',
          attribute: subStatusField,
        }}
      />
    </Box>
  );
}
